import { useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import { useScheduleBoardContext } from 'pos-client/components/scheduleBoard/context/ScheduleBoardContext.tsx';
import { trpc } from 'pos-client/utils/trpc.ts';

export const useTradingTimes = () => {
  const {
    selectedLocation,
    selectedDate,
    setStartDayHour,
    setEndDayHour,
    setTradingTimes,
  } = useScheduleBoardContext();

  const { data: tradingTimes, refetch: refetchTradingTimes } =
    trpc.getAllTradingTimes.useQuery(
      {
        id: selectedLocation?.openingHoursGroupId as string,
      },
      {
        enabled: !!selectedLocation?.openingHoursGroupId,
      }
    );

  const technicianQueryInput = useMemo(() => {
    const start = dayjs(selectedDate).startOf('day').toDate();
    const end = dayjs(selectedDate).endOf('day').toDate();
    return {
      serviceLocationId: selectedLocation?.id ?? '',
      scheduleStart: start,
      scheduleEnd: end,
    };
  }, [selectedLocation?.id, selectedDate]);

  const { data: assignedTechnicians } =
    trpc.locations.getAssignedTechnicians.useQuery(technicianQueryInput, {
      enabled: !!selectedLocation?.id,
    });

  useEffect(() => {
    const selectedDay = dayjs(selectedDate).format('dddd');
    const tradingTime = tradingTimes?.find(
      (tt) => tt.dayOfWeek === selectedDay
    );

    if (!tradingTime) {
      setTradingTimes(null);
      return;
    }

    const opening = dayjs(tradingTime.opening, 'HH:mm:ss');
    const closing = dayjs(tradingTime.closing, 'HH:mm:ss');
    const startCandidates: number[] = [opening.hour()];
    const endCandidates: number[] = [
      closing.hour() + (closing.minute() > 0 ? 1 : 0),
    ];

    (assignedTechnicians?.results ?? []).forEach((technician) => {
      technician.schedules.forEach((schedule) => {
        if (!schedule.start || !schedule.end) return;
        // Shift timestamps arrive already in the location's wall-clock time.
        // Read them as-is (UTC parse) so we don't re-shift them into the
        // viewer's timezone.
        const scheduleStart = dayjs.utc(schedule.start);
        const scheduleEnd = dayjs.utc(schedule.end);
        startCandidates.push(scheduleStart.hour());
        endCandidates.push(
          scheduleEnd.hour() + (scheduleEnd.minute() > 0 ? 1 : 0)
        );
      });
    });

    setTradingTimes({
      start: tradingTime.opening,
      end: tradingTime.closing,
    });

    const start = Math.max(0, Math.min(...startCandidates));
    const end = Math.min(24, Math.max(...endCandidates, start + 1));

    setStartDayHour(start);
    setEndDayHour(end);
  }, [
    selectedDate,
    tradingTimes,
    assignedTechnicians?.results,
    setStartDayHour,
    setEndDayHour,
    setTradingTimes,
  ]);

  return { refetchTradingTimes };
};
